import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useGet } from "../../api";
import {
  IAdvertisersResponse,
  IAffiliateAdvertiser,
  IAffiliateNetworksResponse,
  IAffiliateProduct,
  IProductsResponse,
} from "../../interfaces";
import {
  Button,
  Spinner,
  Badge,
  Table,
  Form,
  Row,
  Col,
  Card,
  OverlayTrigger,
  Tooltip,
  Pagination,
} from "react-bootstrap";
import {
  RefreshCw,
  ExternalLink,
  Copy,
  Check,
  Plus,
  Edit2,
  Trash2,
  ShoppingBag,
} from "react-feather";
import { settingsType, useSettings } from "../../hooks";
import { useSearchParams, Link } from "react-router";
import {
  AffiliateAdvertiserTypeahead,
  ManualProductModal,
  ProductUrlModal,
  useDeleteManualProduct,
} from "./manual-components";

/** Where the row sells: domain, regions, language. Feeds fill these unevenly. */
const provenance = (p: IAffiliateProduct) =>
  [p.merchantDomain, p.regions?.join("/"), p.language]
    .filter(Boolean)
    .join(" · ");

const CopyButton: React.FC<{ text: string; title?: string }> = ({
  text,
  title,
}) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{copied ? "Copied!" : title || "Copy"}</Tooltip>}
    >
      <button
        type="button"
        className="btn btn-link btn-sm p-0 ms-1"
        style={{ lineHeight: 1, verticalAlign: "middle" }}
        onClick={handleCopy}
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </OverlayTrigger>
  );
};

export const Products: React.FC = () => {
  const [searchParams] = useSearchParams();
  const deleteProduct = useDeleteManualProduct();
  const [hasSearched, setHasSearched] = useState(false);

  const [network, setNetwork] = useSettings(settingsType.AP_PRODUCTS_NETWORK);
  const [advertiserId, setAdvertiserId] = useSettings(
    settingsType.AP_PRODUCTS_ADVERTISER_ID
  );
  const [domain, setDomain] = useSettings(settingsType.AP_PRODUCTS_DOMAIN);
  const [categoryFilter, setCategoryFilter] = useSettings(
    settingsType.AP_PRODUCTS_CATEGORY
  );
  const [searchFilter, setSearchFilter] = useSettings(
    settingsType.AP_PRODUCTS_SEARCH
  );
  const [manufacturerFilter, setManufacturerFilter] = useSettings(
    settingsType.AP_PRODUCTS_MANUFACTURER
  );
  const [countryFilter, setCountryFilter] = useSettings(
    settingsType.AP_PRODUCTS_COUNTRY
  );
  const [limit, setLimit] = useState("50");
  const [offset, setOffset] = useState(0);
  const [selectedAdvertiser, setSelectedAdvertiser] =
    useState<IAffiliateAdvertiser | null>(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [editingProduct, setEditingProduct] =
    useState<IAffiliateProduct | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingForcedRefetch, setPendingForcedRefetch] = useState(false);

  const networks = useGet<IAffiliateNetworksResponse>(
    "affiliate-products/networks",
    true
  );
  const networkOptions = useMemo(() => {
    const options = [...(networks.data?.items || [])];
    if (!options.includes("Manual")) {
      options.push("Manual");
    }
    return options;
  }, [networks.data]);

  // "Submitted" state — only updates when user clicks Search or presses Enter
  const [submittedParams, setSubmittedParams] = useState({
    domain: "",
    advertiserId: "",
    network: "",
    q: "",
    category: "",
    manufacturer: "",
    countryCode: "",
  });

  // Initialize from URL params if present
  useEffect(() => {
    const urlNetwork = searchParams.get("network");
    const urlAdvertiserId = searchParams.get("advertiserId");
    const urlCategory = searchParams.get("category");
    const urlDomain = searchParams.get("domain");
    if (urlNetwork) setNetwork(urlNetwork);
    if (urlAdvertiserId) setAdvertiserId(urlAdvertiserId);
    if (urlCategory) setCategoryFilter(urlCategory);
    if (urlDomain) setDomain(urlDomain);
  }, [searchParams]);

  const selectedNetwork = network || "";
  const selectedAdvertiserDetails = useGet<IAffiliateAdvertiser>(
    selectedNetwork && advertiserId
      ? `affiliate-products/advertisers/${selectedNetwork}/${advertiserId}`
      : null,
    false
  );

  useEffect(() => {
    if (selectedAdvertiserDetails.data) {
      setSelectedAdvertiser(selectedAdvertiserDetails.data);
    }
  }, [selectedAdvertiserDetails.data]);

  // A domain can hold one advertiser per geo, all named alike ("Samsung UK"),
  // so the row needs the advertiser's region to say which feed it came from.
  // The product payload carries no advertiser region, so it is joined here.
  const advertiserRegions = useGet<IAdvertisersResponse>(
    submittedParams.domain
      ? `affiliate-products/advertisers?domain=${encodeURIComponent(submittedParams.domain)}&limit=200`
      : null,
    true
  );

  const regionByAdvertiser = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of advertiserRegions.data?.advertisers ?? []) {
      if (a.region) map.set(`${a.affiliateNetwork}:${a.advertiserId}`, a.region);
    }
    if (selectedAdvertiser?.region) {
      map.set(
        `${selectedAdvertiser.affiliateNetwork}:${selectedAdvertiser.advertiserId}`,
        selectedAdvertiser.region
      );
    }
    return map;
  }, [advertiserRegions.data, selectedAdvertiser]);

  // Build query string from submitted params only
  const fetchUrl = useMemo(() => {
    const p = submittedParams;
    const params = new URLSearchParams();
    if (p.domain) params.set("domain", p.domain);
    if (p.advertiserId) params.set("advertiserId", p.advertiserId);
    if (p.network) params.set("network", p.network);
    if (p.q) params.set("q", p.q);
    if (p.category) params.set("category", p.category);
    if (p.manufacturer) params.set("manufacturer", p.manufacturer);
    if (p.countryCode) params.set("countryCode", p.countryCode);
    params.set("offset", String(offset));
    params.set("limit", limit);
    if (!p.domain && !p.advertiserId && !p.network && !p.q) return null;
    return `affiliate-products/products?${params.toString()}`;
  }, [submittedParams, offset, limit]);

  const products = useGet<IProductsResponse>(fetchUrl, !!fetchUrl);

  useEffect(() => {
    if (!pendingForcedRefetch) {
      return;
    }

    // Run refetch after React applies offset/submittedParams updates.
    setPendingForcedRefetch(false);
    products.refetch();
  }, [pendingForcedRefetch, products]);

  const handleSearch = useCallback(() => {
    const next = {
      domain: domain || "",
      advertiserId: advertiserId || "",
      network: selectedNetwork,
      q: searchFilter || "",
      category: categoryFilter || "",
      manufacturer: manufacturerFilter || "",
      countryCode: countryFilter || "",
    };
    const sameSubmittedParams =
      submittedParams.domain === next.domain &&
      submittedParams.advertiserId === next.advertiserId &&
      submittedParams.network === next.network &&
      submittedParams.q === next.q &&
      submittedParams.category === next.category &&
      submittedParams.manufacturer === next.manufacturer &&
      submittedParams.countryCode === next.countryCode;
    const hasQueryCriteria = !!(
      next.domain ||
      next.advertiserId ||
      next.network ||
      next.q
    );

    setOffset(0);
    setHasSearched(true);
    setSubmittedParams(next);

    // Only force refetch when the full request URL would remain unchanged.
    if (sameSubmittedParams && offset === 0 && hasQueryCriteria) {
      setPendingForcedRefetch(true);
    }

    if (selectedNetwork && advertiserId && !selectedAdvertiser) {
      selectedAdvertiserDetails.refetch();
    }
  }, [
    domain,
    advertiserId,
    selectedNetwork,
    searchFilter,
    categoryFilter,
    manufacturerFilter,
    countryFilter,
    submittedParams,
    offset,
    selectedAdvertiser,
    selectedAdvertiserDetails,
  ]);

  const productsList = products.data?.products ?? [];
  const totalCount = products.data?.totalCount ?? 0;
  const limitNumber = Number(limit) || 50;
  const totalPages = Math.max(1, Math.ceil(totalCount / limitNumber));
  const currentPage = Math.floor(offset / limitNumber) + 1;
  const paginationItems = useMemo(() => {
    if (totalPages <= 1) {
      return [] as number[];
    }

    const windowSize = 5;
    const start = Math.max(1, currentPage - Math.floor(windowSize / 2));
    const end = Math.min(totalPages, start + windowSize - 1);
    const adjustedStart = Math.max(1, end - windowSize + 1);
    return Array.from(
      { length: end - adjustedStart + 1 },
      (_, i) => adjustedStart + i
    );
  }, [currentPage, totalPages]);

  const handleClearFilters = () => {
    setDomain("");
    setAdvertiserId("");
    setSelectedAdvertiser(null);
    setCategoryFilter("");
    setSearchFilter("");
    setManufacturerFilter("");
    setCountryFilter("");
    setOffset(0);
    setHasSearched(false);
    setSubmittedParams({
      domain: "",
      advertiserId: "",
      network: "",
      q: "",
      category: "",
      manufacturer: "",
      countryCode: "",
    });
  };

  const handleNextPage = () => {
    const next = offset + limitNumber;
    if (next < totalCount) setOffset(next);
  };

  const handlePrevPage = () => {
    setOffset(Math.max(0, offset - limitNumber));
  };

  const handleGoToPage = (page: number) => {
    const safePage = Math.min(Math.max(1, page), totalPages);
    setOffset((safePage - 1) * limitNumber);
  };

  const handleDeleteProduct = async (product: IAffiliateProduct) => {
    if (
      !window.confirm(
        `Delete product "${product.name}"? This cannot be undone.`
      )
    ) {
      return;
    }

    setActionError(null);
    try {
      await deleteProduct.execute(
        `affiliate-products/products/${product.advertiserId}/${encodeURIComponent(product.id)}`
      );
      await products.refetch();
    } catch (err: any) {
      setActionError(
        err?.data?.error ||
          err?.data?.message ||
          err?.message ||
          "Unknown error"
      );
    }
  };

  return (
    <div>
      <h4 className="mb-3">Products</h4>

      {/* Search & Filter Controls */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2">
            <Col md={3}>
              <Form.Label className="small">Domain</Form.Label>
              <Form.Control
                size="sm"
                placeholder="e.g. skechers.com"
                value={domain}
                onChange={(e) => {
                  setDomain(e.target.value);
                  setOffset(0);
                }}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small">Network</Form.Label>
              <Form.Select
                size="sm"
                value={selectedNetwork}
                onChange={(e) => {
                  setNetwork(e.target.value);
                  setAdvertiserId("");
                  setSelectedAdvertiser(null);
                  setOffset(0);
                }}
              >
                <option value="">All</option>
                {networkOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="small">Advertiser</Form.Label>
              <AffiliateAdvertiserTypeahead
                network={selectedNetwork || undefined}
                value={selectedAdvertiser}
                onChange={(advertiser) => {
                  setSelectedAdvertiser(advertiser);
                  setAdvertiserId(advertiser?.advertiserId || "");
                  if (advertiser?.affiliateNetwork) {
                    setNetwork(advertiser.affiliateNetwork);
                  }
                  setOffset(0);
                }}
                placeholder="Search advertiser..."
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small">Search</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Name or brand..."
                value={searchFilter}
                onChange={(e) => {
                  setSearchFilter(e.target.value);
                  setOffset(0);
                }}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small">Category</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Category..."
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setOffset(0);
                }}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </Col>
          </Row>
          <Row className="g-2 mt-2">
            <Col md={2}>
              <Form.Label className="small">Manufacturer</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Manufacturer..."
                value={manufacturerFilter}
                onChange={(e) => {
                  setManufacturerFilter(e.target.value);
                  setOffset(0);
                }}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <Form.Text className="text-muted" style={{ fontSize: "0.7rem" }}>
                Matches the canonical brand, which most feeds leave empty.
              </Form.Text>
            </Col>
            <Col md={2}>
              <Form.Label className="small">Country</Form.Label>
              <Form.Control
                size="sm"
                placeholder="e.g. UK"
                value={countryFilter}
                onChange={(e) => {
                  setCountryFilter(e.target.value.toUpperCase());
                  setOffset(0);
                }}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                style={{ textTransform: "uppercase" }}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small">Per Page</Form.Label>
              <Form.Select
                size="sm"
                value={limit}
                onChange={(e) => {
                  setLimit(e.target.value);
                  setOffset(0);
                }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </Form.Select>
            </Col>
            <Col md={4} className="d-flex align-items-end gap-2">
              <Button
                size="sm"
                variant="primary"
                onClick={handleSearch}
                disabled={products.loading}
              >
                {products.loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <RefreshCw size={14} className="me-1" /> Search
                  </>
                )}
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={handleClearFilters}
              >
                Clear
              </Button>
              {selectedAdvertiser && (
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => {
                    setEditingProduct(null);
                    setShowProductModal(true);
                  }}
                >
                  <Plus size={14} className="me-1" />
                  Add Product
                </Button>
              )}
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => setShowUrlModal(true)}
              >
                <Plus size={14} className="me-1" />
                Import from URL
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {actionError && (
        <div className="text-danger mb-3">Error: {actionError}</div>
      )}

      {products.error && (
        <div className="text-danger mb-3">Error: {products.error.message}</div>
      )}

      {!hasSearched && (
        <div className="text-muted mb-3">
          Set your filters, then click Search to load products.
        </div>
      )}

      {hasSearched && products.data && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">
              {totalCount > 0
                ? `Showing ${offset + 1}-${Math.min(offset + productsList.length, totalCount)} of ${totalCount} products (page ${currentPage}/${totalPages})`
                : "Showing 0 products"}
            </small>
            <div className="d-flex gap-2">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={offset === 0}
                onClick={handlePrevPage}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={offset + limitNumber >= totalCount}
                onClick={handleNextPage}
              >
                Next
              </Button>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-end mb-2">
              <Pagination size="sm" className="mb-0">
                <Pagination.First
                  disabled={currentPage <= 1}
                  onClick={() => handleGoToPage(1)}
                />
                <Pagination.Prev
                  disabled={currentPage <= 1}
                  onClick={() => handleGoToPage(currentPage - 1)}
                />
                {paginationItems.map((page) => (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handleGoToPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={currentPage >= totalPages}
                  onClick={() => handleGoToPage(currentPage + 1)}
                />
                <Pagination.Last
                  disabled={currentPage >= totalPages}
                  onClick={() => handleGoToPage(totalPages)}
                />
              </Pagination>
            </div>
          )}

          <Table
            striped
            bordered
            hover
            size="sm"
            style={{ fontSize: "0.8rem" }}
          >
            <thead>
              <tr>
                <th style={{ width: "30%" }}>Product</th>
                <th>Manufacturer</th>
                <th>Categories</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Sale</th>
                <th>Links</th>
                <th style={{ width: 90 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {productsList.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div className="d-flex align-items-start">
                      {p.thumbnailUrl && (
                        <img
                          src={p.thumbnailUrl}
                          alt=""
                          style={{
                            width: 40,
                            height: 40,
                            objectFit: "cover",
                            marginRight: 8,
                            borderRadius: 4,
                          }}
                        />
                      )}
                      <div>
                        <Link
                          to={`/affiliate-products/products?network=${encodeURIComponent(p.affiliateNetwork)}&advertiserId=${encodeURIComponent(p.advertiserId)}`}
                          className="fw-bold text-decoration-none"
                        >
                          {p.name}
                        </Link>
                        <CopyButton text={p.id} title="Copy Product ID" />
                        <div
                          className="d-flex align-items-center gap-1"
                          style={{ fontSize: "0.75rem" }}
                        >
                          <Badge
                            bg="dark"
                            title="Affiliate network this product was fetched from"
                          >
                            {p.affiliateNetwork}
                          </Badge>
                          {p.merchantName && (
                            <Link
                              to={`/affiliate-products/advertiser/${encodeURIComponent(p.affiliateNetwork)}/${encodeURIComponent(p.advertiserId)}`}
                              className="text-muted text-decoration-none"
                              title="Advertiser this product came from, with its feed region"
                            >
                              {p.merchantName}
                              {regionByAdvertiser.get(
                                `${p.affiliateNetwork}:${p.advertiserId}`
                              ) &&
                                ` (${regionByAdvertiser.get(
                                  `${p.affiliateNetwork}:${p.advertiserId}`
                                )})`}
                            </Link>
                          )}
                        </div>
                        {provenance(p) && (
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.75rem" }}
                            title="Merchant domain · selling regions · language, as given by the feed"
                          >
                            {provenance(p)}
                          </div>
                        )}
                        {p.sku && (
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.75rem" }}
                          >
                            SKU: {p.sku}
                          </div>
                        )}
                        {p.canonicalKey && (
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.75rem" }}
                          >
                            <span className="font-monospace">
                              {p.canonicalKey}
                            </span>
                            <CopyButton
                              text={p.canonicalKey}
                              title="Copy Canonical Key"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{p.manufacturer || "—"}</td>
                  <td>
                    {p.categories.slice(0, 2).map((c, i) => (
                      <Badge
                        key={i}
                        bg="light"
                        text="dark"
                        className="me-1 mb-1"
                      >
                        {c}
                      </Badge>
                    ))}
                    {p.categories.length > 2 && (
                      <Badge bg="light" text="dark">
                        +{p.categories.length - 2}
                      </Badge>
                    )}
                  </td>
                  <td>
                    <div>
                      <strong>
                        {p.currency} {p.price.toFixed(2)}
                      </strong>
                    </div>
                    {p.isOnSale && p.originalPrice && (
                      <small className="text-muted text-decoration-line-through">
                        {p.originalPrice.toFixed(2)}
                      </small>
                    )}
                  </td>
                  <td>
                    {p.inStock ? (
                      <Badge bg="success">In Stock</Badge>
                    ) : (
                      <Badge bg="danger">Out</Badge>
                    )}
                  </td>
                  <td>
                    {p.isOnSale ? (
                      <Badge bg="warning" text="dark">
                        Sale
                      </Badge>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-2 align-items-center">
                      {p.productUrl && (
                        <a
                          href={p.productUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Affiliate link — goes through the network's tracker"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                      {p.merchantUrl && (
                        <a
                          href={p.merchantUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Merchant product page — direct, untracked"
                        >
                          <ShoppingBag size={14} />
                        </a>
                      )}
                      {!p.productUrl && !p.merchantUrl && (
                        <span className="text-muted">—</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {p.source === "Manual" ? (
                      <div className="d-flex gap-1">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            setEditingProduct(p);
                            setShowProductModal(true);
                          }}
                        >
                          <Edit2 size={11} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDeleteProduct(p)}
                        >
                          <Trash2 size={11} />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {productsList.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted">
                    No products found. Try a different domain or filter.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </>
      )}

      {selectedAdvertiser && (
        <ManualProductModal
          show={showProductModal}
          advertiser={selectedAdvertiser}
          product={editingProduct}
          onHide={() => setShowProductModal(false)}
          onSaved={async () => {
            await products.refetch();
          }}
        />
      )}

      <ProductUrlModal
        show={showUrlModal}
        advertiser={selectedAdvertiser}
        onHide={() => setShowUrlModal(false)}
        onSaved={async () => {
          await products.refetch();
        }}
      />
    </div>
  );
};
